"use client";

import { c as _c } from "react/compiler-runtime";
import { LoginFormContent } from "@/components/login-dialog/form";
import { PostLoginChoiceDialog } from "@/components/post-login-choice-dialog";
import { Button } from "@/components/rac/button";
import { DialogContent, DialogOverlay, DialogTrigger } from "@/components/rac/dialog";
import { Sheet, SheetContent, SheetOverlay } from "@/components/rac/sheet";
import { useLoginForm } from "@/hooks/use-login-form";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useLoginDialogStore } from "@/stores/login-dialog-store";
import { X } from "lucide-react";
import Image from "next/image";
import { useEffect } from "react";

/**
 * LoginDialog — a render component that reads from the Zustand login-dialog-store
 * and renders the login dialog/sheet UI. No longer a Context provider.
 *
 * Mount this component once in the app tree (e.g. in layout.tsx).
 * Consumers call `useLoginDialogStore` (or the `useLoginDialog` hook wrapper)
 * to open/close the dialog from anywhere.
 */
export function LoginDialog() {
  const $ = _c(25);
  const {
    isOpen,
    dialogOptions,
    setIsOpen
  } = useLoginDialogStore();
  const isMobile = useMediaQuery("(max-width: 768px)");
  let t0;
  if ($[0] !== dialogOptions.isShort || $[1] !== dialogOptions.onLoginSuccess || $[2] !== setIsOpen) {
    t0 = {
      onLoginSuccess: dialogOptions.onLoginSuccess,
      setIsOpen,
      isShort: dialogOptions.isShort
    };
    $[0] = dialogOptions.isShort;
    $[1] = dialogOptions.onLoginSuccess;
    $[2] = setIsOpen;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const formProps = useLoginForm(t0);
  let t1;
  let t2;
  if ($[4] !== dialogOptions.isShort || $[5] !== formProps || $[6] !== isOpen) {
    t1 = () => {
      if (!isOpen && !dialogOptions.isShort) {
        const timer = setTimeout(() => {
          formProps.resetForm();
        }, 300);
        return () => clearTimeout(timer);
      }
    };
    t2 = [isOpen, formProps, dialogOptions.isShort];
    $[4] = dialogOptions.isShort;
    $[5] = formProps;
    $[6] = isOpen;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  useEffect(t1, t2);
  let t3;
  if ($[9] !== dialogOptions.isShort || $[10] !== formProps || $[11] !== isMobile || $[12] !== isOpen || $[13] !== setIsOpen) {
    const renderDialog = () => {
      if (!isOpen) {
        return null;
      }
      if (dialogOptions.isShort) {
        return <LoginFormContent {...formProps} isMobile={isMobile} />;
      }
      if (isMobile) {
        return <Sheet isOpen={isOpen} onOpenChange={setIsOpen}><SheetOverlay className="z-10000!"><SheetContent side="bottom" className="h-auto max-h-[90dvh] rounded-t-2xl bg-gradient-to-br from-orange-100 via-red-50 to-pink-50" closeButton={false}><div className="relative"><Button variant="bareshell" onClick={() => setIsOpen(false)} className="absolute -top-14 right-4 p-2 z-1 rounded-full bg-gray-100 hover:bg-gray-200 shadow-lg transition-colors" aria-label="Close dialog"><X className="h-6 w-6 text-gray-800" /></Button></div><div className="flex-1 overflow-y-auto"><LoginFormContent {...formProps} isMobile={true} /></div></SheetContent></SheetOverlay></Sheet>;
      }
      return <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}><DialogOverlay className="bg-black/50 z-10000!"><div className="relative max-w-4xl"><DialogContent className="max-w-4xl w-full h-[600px] md:h-[600px] sm:h-full sm:max-h-[90dvh] bg-transparent border-0 shadow-none p-0" closeButton={false}><Button variant="bareshell" onClick={() => setIsOpen(false)} className="absolute -right-4 -top-4 p-2 z-1 rounded-full bg-gray-100 hover:bg-gray-200 shadow-lg transition-colors" aria-label="Close dialog"><X className="h-6 w-6 text-gray-800" /></Button><div className="p-0 overflow-hidden bg-white border-0 shadow-2xl sm:rounded-2xl rounded-none mb-8"><div className="flex flex-col md:flex-row h-full" data-testid="login-dialog" id="login-dialog"><div className="md:w-1/2 bg-gradient-to-br from-red-500 via-red-400 to-red-600 relative overflow-hidden"><div className="absolute inset-0 opacity-20"><div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/10 to-transparent" /></div><div className="flex flex-col items-center justify-center h-full p-6 md:p-8"><div className="text-center max-w-sm"><div className="relative h-40 mb-6"><Image src="/images/login/unlock.svg" alt="Login illustration" fill={true} className="object-contain" /></div><h3 className="text-lg font-semibold text-white mb-2">Welcome to Settlin</h3><p className="text-sm text-white/90">Unlock your perfect home with our personalized property recommendations</p></div></div></div><div className="flex-1 bg-gradient-to-br from-orange-100 via-red-50 to-pink-50 flex flex-col justify-center px-8 py-8 relative"><LoginFormContent {...formProps} /></div></div></div></DialogContent></div></DialogOverlay></DialogTrigger>;
    };
    t3 = renderDialog();
    $[9] = dialogOptions.isShort;
    $[10] = formProps;
    $[11] = isMobile;
    $[12] = isOpen;
    $[13] = setIsOpen;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== formProps) {
    t4 = () => formProps.setShowRelationshipGuard(false);
    $[15] = formProps;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const t5 = formProps.currentUser?.relation === "buyer" || formProps.currentUser?.relation === "seller" ? formProps.currentUser.relation : "buyer";
  let t6;
  if ($[17] !== formProps.onRelationshipSelected || $[18] !== formProps.showRelationshipGuard || $[19] !== t4 || $[20] !== t5) {
    t6 = <PostLoginChoiceDialog isOpen={formProps.showRelationshipGuard} onClose={t4} currentRelationship={t5} featureName="Choose Your Role" description="Tell us how you'd like to use Settlin today" onComplete={formProps.onRelationshipSelected} />;
    $[17] = formProps.onRelationshipSelected;
    $[18] = formProps.showRelationshipGuard;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t3 || $[23] !== t6) {
    t7 = <>{t3}{t6}</>;
    $[22] = t3;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
}